import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { jobsApi, statsApi } from '@/lib/api';
import NotificationsDropdown from '@/components/NotificationsDropdown';
import { 
  Truck, 
  Plus, 
  MapPin, 
  Calendar,
  Package,
  DollarSign,
  Clock,
  LogOut,
  LayoutDashboard,
  FileText,
  CheckCircle,
  Loader2,
  ArrowRight,
  MessageSquare
} from 'lucide-react';

const statusColors = {
  posted: 'bg-orange-500/15 text-orange-500',
  quoted: 'bg-blue-500/15 text-blue-500',
  accepted: 'bg-green-500/15 text-green-500',
  in_progress: 'bg-yellow-500/15 text-yellow-600',
  completed: 'bg-emerald-500/15 text-emerald-600',
  cancelled: 'bg-red-500/15 text-red-500',
  disputed: 'bg-purple-500/15 text-purple-500'
};

const JobCard = ({ job, onClick }) => (
  <motion.div
    className="job-card card-hover cursor-pointer"
    onClick={onClick}
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ y: -2 }}
  >
    <div className="job-card-header flex items-center justify-between">
      <h3 className="font-semibold text-base truncate pr-4" style={{ fontFamily: 'Barlow Condensed' }}>
        {job.title}
      </h3>
      <Badge className={`${statusColors[job.status]} text-xs uppercase`}>
        {job.status.replace('_', ' ')}
      </Badge>
    </div>
    <div className="job-card-body space-y-4">
      <div className="route-line">
        <div className="route-point">
          <p className="text-sm font-medium">{job.pickup_city}, {job.pickup_state}</p>
          <p className="text-xs text-muted-foreground">{job.pickup_location}</p>
        </div>
        <div className="route-point destination">
          <p className="text-sm font-medium">{job.delivery_city}, {job.delivery_state}</p>
          <p className="text-xs text-muted-foreground">{job.delivery_location}</p>
        </div>
      </div>
      
      <div className="flex items-center justify-between pt-2 border-t border-border">
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Package className="w-3.5 h-3.5" />
            {job.weight_kg} kg
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {new Date(job.pickup_date).toLocaleDateString()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs font-medium">{job.quote_count}/5 quotes</span>
        </div>
      </div>
    </div>
  </motion.div>
);

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [jobsRes, statsRes] = await Promise.all([
          jobsApi.getAll(),
          statsApi.getDashboard()
        ]);
        setJobs(jobsRes.data);
        setStats(statsRes.data);
      } catch (error) {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredJobs = jobs.filter(job => {
    if (activeTab === 'all') return true;
    if (activeTab === 'active') return ['posted', 'quoted', 'accepted', 'in_progress'].includes(job.status);
    if (activeTab === 'completed') return job.status === 'completed';
    return true;
  });

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container-app py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="bg-gray-900 rounded-full p-1.5 shadow-lg ring-2 ring-primary/30">
                <img 
                  src="/thm-logo.png" 
                  alt="The Hotshot Marketplace" 
                  className="h-10 w-auto"
                />
              </div>
            </Link>
          </div>
          
          <div className="flex items-center gap-4">
            <Link to="/post-job">
              <Button className="btn-active gap-2" data-testid="post-job-btn" data-tour="post-job-btn">
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Post Job</span>
              </Button>
            </Link>
            <NotificationsDropdown />
            <div className="flex items-center gap-3 pl-4 border-l border-border" data-tour="notifications-btn">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground">Customer</p>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container-app py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            WELCOME BACK, {user?.full_name?.split(' ')[0]?.toUpperCase()}
          </h1>
          <p className="text-muted-foreground">
            Manage your hotshot jobs and track deliveries
          </p>
        </div>

        {/* Stats Grid */}
        <div className="dashboard-grid mb-8" data-tour="dashboard-stats">
          <motion.div 
            className="stat-card"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="stat-value" data-testid="total-jobs-stat">{stats?.total_jobs || 0}</p>
                <p className="stat-label">Total Jobs</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="stat-card"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-500/10 rounded-sm flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <p className="stat-value" data-testid="active-jobs-stat">{stats?.active_jobs || 0}</p>
                <p className="stat-label">Active Jobs</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="stat-card"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/10 rounded-sm flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="stat-value" data-testid="completed-jobs-stat">{stats?.completed_jobs || 0}</p>
                <p className="stat-label">Completed</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="stat-card"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500/10 rounded-sm flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="stat-value price-display" data-testid="total-spent-stat">
                  A${stats?.total_spent?.toLocaleString() || '0'}
                </p>
                <p className="stat-label">Total Spent</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Jobs Section */}
        <div className="bg-card border border-border rounded-sm" data-tour="jobs-list">
          <div className="p-6 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
              YOUR JOBS
            </h2>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
                <TabsTrigger value="active" data-testid="tab-active">Active</TabsTrigger>
                <TabsTrigger value="completed" data-testid="tab-completed">Completed</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="p-6">
            {filteredJobs.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-muted rounded-sm flex items-center justify-center mx-auto mb-4">
                  <Package className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  NO JOBS YET
                </h3>
                <p className="text-muted-foreground mb-6">
                  Post your first job to get started
                </p>
                <Link to="/post-job">
                  <Button className="btn-active gap-2" data-testid="empty-post-job-btn">
                    <Plus className="w-4 h-4" />
                    Post Your First Job
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredJobs.map((job) => (
                  <JobCard 
                    key={job.id} 
                    job={job} 
                    onClick={() => navigate(`/job/${job.id}`)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
